import { notification as antdNotification } from 'antd';
import type { NotificationArgsProps } from 'antd';
import { User } from '../services/auth';

// Store user reference for non-hook usage
let currentUser: User | null = null;

// Default notification configuration
const DEFAULT_CONFIG: NotificationArgsProps = {
  placement: 'top',
};

// Apply default config on module load
antdNotification.config(DEFAULT_CONFIG);

/**
 * Set the current user for notification preference checking
 * This should be called from AuthContext when user changes
 */
export const setNotificationUser = (user: User | null) => {
  currentUser = user;
};

/**
 * Custom notification wrapper that respects user preferences
 * Mimics antd's notification API with dot notation
 */
export const notification = {
  /**
   * Show a success notification (respects user preference)
   */
  success: (args: NotificationArgsProps) => {
    // Show notification if user preference is enabled (default to true if not set)
    if (currentUser?.show_success_notifications !== false) {
      antdNotification.success(args);
    }
  },

  /**
   * Show an error notification (always shown)
   */
  error: (args: NotificationArgsProps) => {
    antdNotification.error(args);
  },

  /**
   * Show an info notification (always shown)
   */
  info: (args: NotificationArgsProps) => {
    antdNotification.info(args);
  },

  /**
   * Show a warning notification (always shown)
   */
  warning: (args: NotificationArgsProps) => {
    antdNotification.warning(args);
  },

  /**
   * Configure notification settings
   * Merges with default config (placement: 'top')
   */
  config: (config: NotificationArgsProps) => {
    antdNotification.config({ ...DEFAULT_CONFIG, ...config });
  },

  /**
   * Destroy all notifications
   */
  destroy: antdNotification.destroy,

  /**
   * Open a notification with custom type
   */
  open: antdNotification.open,
};
